import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Divider, Grid, makeStyles, Typography } from '@material-ui/core'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { get } from 'lodash'
import clsx from 'clsx'
import { useQueryClient } from '@tanstack/react-query'
import Card from '../../../../molecules/Card'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import FlexRow from '../../../../molecules/FlexRow'
import Icon from '../../../../atoms/Icon'
import { ICON_NAMES } from '../../../../../constants'
import EntryContentField from '../../../WealthJourney/v3/EntryContent/EntryContentField'
import { useAssignedAttachmentsByClient } from '../../../../../api/documentVault'
import TypeChip from './TypeChip'
import EntryMenu from './EntryMenu'
import { useDDPWealthJourneyEntryListState } from './DDPWealthJourneyEntryListContext'
import DDPWealthJourneyDocumentItem from './DDPWealthJourneyAttachmentItem'

dayjs.extend(utc)

const useStyles = makeStyles(theme => ({
  entryContent: {
    '& p': {
      fontSize: theme.typography.fontSizes.xxl,
      fontWeight: theme.typography.weights.light
    },
    '& .__tbp10': {
      paddingBottom: theme.layout.padding.p10
    }
  },
  description: {
    fontSize: theme.typography.fontSizes.xxl
  },
  taskList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.layout.gap.g10
  },
  taskListItem: {
    fontSize: theme.typography.fontSizes.xl,
    fontWeight: theme.typography.weights.light
  }
}))

function DDPWealthJourneyContent ({
  className,
  style,
  entry: _entry,
  showTypeChip = true,
  entryTypeAccessor = 'entryType',
  entryTypeChipProps = {},
  showMenu = true,
  showAttachments = true,
  showTasks = true,
  showFields = true,
  leftColumnSize = 8,
  rightColumnSize = 4
}) {
  const classes = useStyles()
  const { selectedEntry } = useDDPWealthJourneyEntryListState()
  const entry = _entry || selectedEntry

  const { data: attachmentsData } = useAssignedAttachmentsByClient(1500, entry?.entryId, entry?.clientId, {
    enabled: showAttachments && !!entry?.entryId && !!entry?.clientId,
    select: (data) => data?.attachments?.map(({ document }) => document) || []
  })

  // Extract entry fields from entry type attributes (same as v3)
  const entryFields = useMemo(() => {
    if (!entry || !showFields) return []

    return (entry.entryType?.attributes || []).map(attr => {
      return {
        value: get(entry, attr.accessor),
        key: attr.accessor,
        label: attr.label,
        description: attr.description,
        ordinal: attr.ordinal,
        data: attr.data,
        type: attr.type
      }
    }).filter(field => !!field.value)
      .sort((a, b) => a.ordinal - b.ordinal)
  }, [entry, showFields])

  // Extract data from entry
  const title = entry?.entryJson?.title || entry?.entryText
  const tasks = showTasks ? (entry?.entryJson?.tasks || []) : []
  const documents = useMemo(() => showAttachments ? (entry?.attachments || attachmentsData || []) : [], [entry, attachmentsData, showAttachments])

  const hasLeft = entryFields.length > 0
  const hasRight = tasks.length > 0 || documents.length > 0
  const hasContent = hasLeft || hasRight

  const targetDate = useMemo(() => {
    if (!entry?.entryDate) return ''
    const entryDate = dayjs.utc(entry.entryDate)
    return entryDate.format('MMM D, YYYY')
  }, [entry?.entryDate])

  // need to make sure the content also updates when entry changes
  const queryClient = useQueryClient()
  const handleUpdate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['core/execute-data-pipeline'], refetchType: 'all' })
  }

  if (!entry) return null

  return (
    <FadeIn className={clsx(className, classes.entryContent)} style={style}>
      <Card>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FlexRow style={{ justifyContent: 'space-between' }}>
              <Typography variant='h2'>{title}</Typography>
              <FlexRow>
                {showTypeChip && <TypeChip {...entryTypeChipProps} entryType={get(entry, entryTypeAccessor)} />}
                {showMenu && <EntryMenu entry={entry} size='24px' onUpdate={handleUpdate} />}
              </FlexRow>
            </FlexRow>
            <FlexRow>
              <div className={classes.date}>{targetDate}</div>
            </FlexRow>
          </Grid>

          {hasContent && (
            <Grid item xs={12}>
              <Divider />
            </Grid>
          )}

          <Grid item xs={!hasRight ? 12 : leftColumnSize}>
            {hasLeft && (
              <Grid container spacing={1}>
                {entryFields.map(ef => (
                  <Grid key={ef.key} item xs={12}>
                    <EntryContentField field={ef} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
          {hasRight && (
            <Grid item xs={rightColumnSize}>
              <Grid container spacing={3}>
                {documents.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant='h3'>Attachments</Typography>
                    <Card>
                      {documents.map((document, idx) => (
                        <DDPWealthJourneyDocumentItem
                          key={document?.documentId ?? idx}
                          document={document}
                        />
                      ))}
                    </Card>
                  </Grid>
                )}

                {tasks.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant='h3'>Tasks</Typography>
                    <Card>
                      <div className={classes.taskList}>
                        {tasks.map(task => (
                          <div key={task.id} className={classes.taskListItem}>
                            <FlexRow style={{ alignItems: 'center', gap: '20px' }}>
                              <Icon
                                name={task.status === 'complete' ? ICON_NAMES.radioChecked : ICON_NAMES.radioUnchecked}
                              />
                              <div>{task.description}</div>
                            </FlexRow>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        </Grid>
      </Card>
    </FadeIn>
  )
}

DDPWealthJourneyContent.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  entry: PropTypes.object,
  showTypeChip: PropTypes.bool,
  entryTypeAccessor: PropTypes.string,
  entryTypeChipProps: PropTypes.object,
  showMenu: PropTypes.bool,
  showAttachments: PropTypes.bool,
  showTasks: PropTypes.bool,
  showFields: PropTypes.bool,
  leftColumnSize: PropTypes.number,
  rightColumnSize: PropTypes.number
}

export default DDPWealthJourneyContent
